import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createVNode as _createVNode, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "container" }
const _hoisted_2 = ["onMouseenter"]
const _hoisted_3 = { key: 0 }

import { computed, ref } from 'vue'
import TooltipStandardV2 from '../atoms/TooltipStandardV2.vue'
import ChartTooltip from '../atoms/ChartTooltip.vue'
import ColorLegendContinuous from '@/design-system/atoms/ColorLegendContinuous.vue'
import { ColorScaleInterpolate, getColorForValue } from '@/utils/colorScale'
import TableBase from '../atoms/TableBase.vue'
import { FormatOptions, formatter } from '@/components/AnalyticsDashboard/formatter'
import { useI18n } from 'vue-i18n'
import { HeatmapResponse, HeatmapSplitBy } from '@/api-client/models/indoorclimate/Contracts'
import TextBase from '../atoms/TextBase.vue'
import { useEntityLookup } from '@/composables/useEntityLookup'
import { capitalizeFirstLetter } from '@/utils/capitalizeFirstLetter'
import { LocaleString } from '@/api-client/models/utilization/Contracts'
import { useHoverStore } from '@/pinia/hover'

type Props = {
  matrix?: HeatmapResponse
  xSumLabel?: string
  ySumLabel?: string
  tooltipLabel: string
  fullscreen?: boolean
  colorScale: ColorScaleInterpolate
  dataFormat?: FormatOptions
  timeFormat?: FormatOptions
  start: number
  end: number
  subMeasurementToTitle: Record<string, LocaleString>
}

type HeaderLabel = {
  title: string
  subtitle?: string
}

type Cell = {
  value: number | null
  color: string
  x: number
  y: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HeatmapTable',
  props: {
    matrix: {},
    xSumLabel: {},
    ySumLabel: {},
    tooltipLabel: {},
    fullscreen: { type: Boolean },
    colorScale: {},
    dataFormat: {},
    timeFormat: {},
    start: {},
    end: {},
    subMeasurementToTitle: {}
  },
  setup(__props: any) {

const { locale } = useI18n()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

const props = __props

const { findEntity } = useEntityLookup()

const table = computed(() =>
  props.matrix
    ? {
        body: props.matrix.matrix.map((row) =>
          paginate(row).map((value) => {
            const color =
              value !== undefined && value !== null
                ? getColorForValue(props.colorScale, value).toHex()
                : '#fff'
            return {
              value,
              color,
              style: { backgroundColor: color },
              class: 'custom-cell'
            }
          })
        ),
        headers: {
          top: paginate(props.matrix.axes.x).map((label) => ({
            label: {
              title: label ? formatter(label, locale.value, props.timeFormat) : '-'
            } as HeaderLabel,
            class: 'label-x'
          })),
          left: props.matrix.axes.y.map((id) => ({
            label:
              props.matrix?.splitBy && id ? translateIdToName(id, props.matrix.splitBy) : undefined,
            id,
            class: 'label-y'
          })),
          bottomLeft: { label: { title: props.xSumLabel } as HeaderLabel, class: 'label-x' },
          bottom:
            props.ySumLabel !== undefined
              ? paginate(props.matrix.summary.x).map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined,
          topRight:
            props.xSumLabel !== undefined
              ? { label: { title: props.ySumLabel } as HeaderLabel, class: 'label-x' }
              : undefined,
          right:
            props.xSumLabel !== undefined
              ? props.matrix.summary.y.map((sum) => ({
                  label: {
                    title: sum !== null ? formatter(sum, locale.value, props.dataFormat) : '-'
                  } as HeaderLabel,
                  class: 'custom-sum'
                }))
              : undefined
        }
      }
    : undefined
)

const hoveredCell = ref<Cell | undefined>()

const mouseEnterCell = (
  { value, color }: { value: number | null; color: string | undefined },
  centerX: number,
  centerY: number
) => {
  if (value === null || color === undefined) {
    return
  }
  hoveredCell.value = {
    value,
    color,
    x: centerX,
    y: centerY
  }
}

const mouseLeaveCell = () => {
  hoveredCell.value = undefined
}

function translateIdToName(id: string, splitBy: HeatmapSplitBy): HeaderLabel | undefined {
  if (splitBy === 'SUB_MEASUREMENT') {
    return {
      title: props.subMeasurementToTitle?.[id as string]?.[locale.value]
    }
  } else {
    const entity = findEntity(id)
    const entityName = entity?.properties.name
    let subtitle = undefined
    if (splitBy === 'SPACE') {
      subtitle = entity?.properties.aliasId
    } else if (splitBy === 'CONSTRUCTION') {
      subtitle = entity?.properties.marking
    }
    return entity
      ? {
          title: capitalizeFirstLetter(entityName),
          subtitle
        }
      : { title: id }
  }
}

function paginate<T>(array: T[]): T[] {
  return array.slice(Math.max(0, props.start), Math.min(array.length, props.end))
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (hoveredCell.value)
        ? (_openBlock(), _createBlock(ChartTooltip, {
            key: 0,
            label: props.tooltipLabel,
            color: hoveredCell.value.color,
            value: 
          hoveredCell.value.value !== undefined && hoveredCell.value.value !== null
            ? _unref(formatter)(hoveredCell.value.value, _unref(locale), _ctx.dataFormat)
            : '-'
        ,
            x: hoveredCell.value.x,
            y: hoveredCell.value.y
          }, null, 8, ["label", "color", "value", "x", "y"]))
        : _createCommentVNode("", true)
    ])),
    (table.value && table.value.body.length > 0)
      ? (_openBlock(), _createBlock(TableBase, {
          key: 0,
          table: table.value,
          sortBy: (i) => table.value?.headers.left?.[i]?.label?.title ?? '',
          sortDirection: _ctx.matrix?.splitBy === 'LEVEL' ? 'desc' : 'asc',
          bodyHeight: "400px",
          leftWidth: "120px",
          layout: "fixed",
          rightTextAlign: "center",
          caption: "Heatmap table",
          onMouseEnterCell: mouseEnterCell,
          onMouseLeaveCell: mouseLeaveCell
        }, {
          "header-cell": _withCtx(({ cell: { label, id }, position }) => [
            (position !== 'left')
              ? (_openBlock(), _createBlock(TextBase, {
                  key: 0,
                  text: label?.title,
                  textWrap: "nowrap",
                  type: position === 'right' || position === 'bottom' ? 'label' : 'tiny'
                }, null, 8, ["text", "type"]))
              : (_openBlock(), _createElementBlock("div", {
                  key: 1,
                  style: { cursor: 'pointer' },
                  onMouseenter: () => _unref(addHoveredItem)(id, { color: '#fadb14' }),
                  onMouseleave: _cache[0] || (_cache[0] = 
//@ts-ignore
(...args) => (_unref(removeLastHoveredItem) && _unref(removeLastHoveredItem)(...args)))
                }, [
                  label
                    ? (_openBlock(), _createBlock(TooltipStandardV2, {
                        key: 0,
                        style: { height: '100%' },
                        text: `${label.title}${label?.subtitle ? '\n(' + label.subtitle + ')' : ''}`,
                        disabledTooltip: position !== 'left',
                        heatmap: "",
                        "arrow-position": "top-center"
                      }, {
                        default: _withCtx(() => [
                          _createElementVNode("div", null, [
                            _createVNode(TextBase, {
                              text: label.title,
                              type: "tiny",
                              overflow: "ellipsis"
                            }, null, 8, ["text"])
                          ]),
                          (label?.subtitle)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                                _createVNode(TextBase, {
                                  text: label.subtitle,
                                  type: "tiny",
                                  color: "secondary"
                                }, null, 8, ["text"])
                              ]))
                            : _createCommentVNode("", true)
                        ]),
                        _: 2
                      }, 1032, ["text", "disabledTooltip"]))
                    : _createCommentVNode("", true)
                ], 40, _hoisted_2))
          ]),
          footer: _withCtx(() => [
            _createVNode(ColorLegendContinuous, {
              formatOptions: _ctx.dataFormat,
              colorScale: _ctx.colorScale
            }, null, 8, ["formatOptions", "colorScale"])
          ]),
          _: 1
        }, 8, ["table", "sortBy", "sortDirection"]))
      : _createCommentVNode("", true)
  ]))
}
}

})